import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { RotateCcw, Trash2, AlertTriangle } from 'lucide-mui';
import { RootState } from '../../reducers';
import {
  loadBackupRecordsThunk,
  uploadBackupRecordThunk,
  deleteBackupRecordThunk,
} from './settingsSlice';
import { ToggleSetting, SettingsSection } from './common';

interface BackupRecord {
  fileId: string;
  startedAt: number;
}

const RecoveryPage: React.FC = () => {
  const dispatch = useDispatch();
  const { backupRecords: records, recoveryState, status } = useSelector(
    (state: RootState) => state.settings.recovery
  );

  useEffect(() => {
    dispatch(loadBackupRecordsThunk());
  }, [status]);

  const handleRecover = (record: BackupRecord) => {
    dispatch(uploadBackupRecordThunk(record));
  };

  const handleDelete = (fileId: string) => {
    dispatch(deleteBackupRecordThunk(fileId));
  };

  if (status === 'idle') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        overflow: 'auto',
        bgcolor: 'transparent',
        px: 3,
        pt: 1,
        pb: 3,
        '& > *:not(:last-child)': {
          mb: 3,
        },
      }}
    >
      <SettingsSection title="File Recovery">
        <ToggleSetting
          checked={false}
          onChange={() => {}}
          label="Recover Lost Files"
          description="If a recording is not uploaded, we can attempt to locate a fractured or incomplete recording from either the browser cache or our servers. Please note that in some circumstances, these files cannot be recovered due to connectivity issues."
          icon={<AlertTriangle />}
        >
          <Box sx={{ mt: 2 }}>
            <TableContainer
              sx={{
                width: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <RotateCcw
                          sx={{
                            width: 16,
                            height: 16,
                            color: 'text.secondary',
                          }}
                        />
                        File ID
                      </Box>
                    </TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((record) => (
                    <TableRow key={record.fileId}>
                      <TableCell>{record.fileId}</TableCell>
                      <TableCell>
                        {new Date(record.startedAt).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'flex-end',
                          }}
                        >
                          <Tooltip title="Recover">
                            <IconButton
                              onClick={() => handleRecover(record)}
                              disabled={
                                recoveryState[record.fileId]?.status ===
                                'ongoing'
                              }
                              color="secondary"
                              size="small"
                            >
                              {recoveryState[record.fileId]?.status ===
                              'ongoing' ? (
                                <CircularProgress size={20} color="secondary" />
                              ) : (
                                <RotateCcw sx={{ width: 20, height: 20 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => handleDelete(record.fileId)}
                              color="secondary"
                              size="small"
                            >
                              <Trash2 sx={{ width: 20, height: 20 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {records.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        align="center"
                        sx={{ color: 'text.secondary' }}
                      >
                        No recoverable files found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </ToggleSetting>
      </SettingsSection>
    </Box>
  );
};

export default RecoveryPage;
